import { FormGroup } from '@angular/forms';

import { Media } from '@eiq/files/models';

import { IBlockForm } from './block-form.interface';
import { LoopStimulusMedia } from './loop-stimulus-media.interface';

/** Edits the authored catalogue; presentation and filtering never change its order. */
export class LoopMediaCatalogue {
  constructor(private readonly form: FormGroup<IBlockForm>) {}

  add(media: Media): void {
    this.addMany([media]);
  }

  addMany(media: Media[]): void {
    if (!media.length) {
      return;
    }
    const entries = this.entries();
    const ids = new Set(entries.map((entry) => entry.mediaId));
    const lastIndex = this.form.controls.lastSourceIndex.value ?? 0;
    const added = media.map((entry, index) => {
      if (ids.has(entry.mediaId)) {
        throw new Error('Duplicate loop media source');
      }
      ids.add(entry.mediaId);
      return this.newEntry(entry, lastIndex + index + 1);
    });
    this.form.controls.lastSourceIndex.setValue(lastIndex + added.length, { emitEvent: false, onlySelf: true });
    this.form.controls.sourceMedia.setValue([...entries, ...added]);
  }

  replace(mediaId: string, media: Media): void {
    const entries = this.entries();
    const position = entries.findIndex((entry) => entry.mediaId === mediaId);
    if (position < 0) {
      return;
    }
    if (entries.some((entry) => entry.mediaId === media.mediaId)) {
      throw new Error('Duplicate loop media source');
    }
    // Replacing the asset keeps the stimulus slot and its answer identity.
    const replacement = this.newEntry(media, entries[position].sourceIndex);
    this.form.controls.sourceMedia.setValue(entries.map((entry, index) => (index === position ? replacement : entry)));
  }

  rename(mediaId: string, name: string): void {
    this.form.controls.sourceMedia.setValue(
      this.entries().map((entry) => {
        if (entry.mediaId !== mediaId) {
          return entry;
        }
        return { ...entry, name };
      })
    );
  }

  remove(mediaIds: ReadonlySet<string>): void {
    this.form.controls.sourceMedia.setValue(this.entries().filter((entry) => !mediaIds.has(entry.mediaId)));
  }

  private entries(): LoopStimulusMedia[] {
    return this.form.controls.sourceMedia.value ?? [];
  }

  private newEntry(media: Media, sourceIndex: number): LoopStimulusMedia {
    if (!Number.isSafeInteger(sourceIndex) || sourceIndex < 1) {
      throw new Error('Invalid last source index');
    }
    return { mediaId: media.mediaId, name: media.name, originalFilename: media.name, type: media.type, sourceIndex };
  }
}
